import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Monitor, Tablet, Smartphone } from 'lucide-react';
import { api } from '../../api.ts';
import { SiteConfigRenderer } from '../LandingPage.tsx';
import type { SiteConfig } from '../../siteContentTypes.ts';
import { GeneralSection } from './appearance/GeneralSection.tsx';
import { BrandingSection } from './appearance/BrandingSection.tsx';
import { HeaderSection } from './appearance/HeaderSection.tsx';
import { HeroSection } from './appearance/HeroSection.tsx';
import { TrustLogosSection } from './appearance/TrustLogosSection.tsx';
import { StatisticsSection } from './appearance/StatisticsSection.tsx';
import { HowItWorksSection } from './appearance/HowItWorksSection.tsx';
import { VideoConfigSection } from './appearance/VideoConfigSection.tsx';
import { FeatureConfigSection } from './appearance/FeatureConfigSection.tsx';
import { MatchingSection } from './appearance/MatchingSection.tsx';
import { TestimonialsSection } from './appearance/TestimonialsSection.tsx';
import { BeforeAfterSection } from './appearance/BeforeAfterSection.tsx';
import { FinalCtaSection } from './appearance/FinalCtaSection.tsx';
import { FaqSection } from './appearance/FaqSection.tsx';
import { FooterSection } from './appearance/FooterSection.tsx';
import { SeoSection } from './appearance/SeoSection.tsx';
import { SectionsOrderSection } from './appearance/SectionsOrderSection.tsx';
import { AdvancedSection } from './appearance/AdvancedSection.tsx';
import { HistorySection } from './appearance/HistorySection.tsx';

const CATEGORIES = [
  'Général',
  'Identité visuelle',
  'Navigation',
  'Hero',
  'Sections & ordre',
  'Logos partenaires',
  'Chiffres',
  'Comment ça marche',
  'Vidéo',
  'Section Extras',
  'Section Restaurateurs',
  'Matching',
  'Témoignages',
  'Avant / après',
  'CTA final',
  'FAQ',
  'Footer',
  'SEO',
  'Personnalisation avancée',
  'Historique',
] as const;

type Category = (typeof CATEGORIES)[number];

const VIEWPORTS: Record<'desktop' | 'tablet' | 'mobile', number | string> = {
  desktop: '100%',
  tablet: 820,
  mobile: 390,
};

export function AppearanceEditor() {
  const [config, setConfig] = useState<SiteConfig | null>(null);
  const [category, setCategory] = useState<Category>('Général');
  const [viewport, setViewport] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [note, setNote] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  function load() {
    api.get<{ config: SiteConfig }>('/site-content/draft').then((res) => {
      setConfig(res.config);
      setDirty(false);
    });
  }

  useEffect(load, []);

  function showToast(message: string) {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  }

  function update(patch: Partial<SiteConfig>) {
    setConfig((prev) => (prev ? { ...prev, ...patch } : prev));
    setDirty(true);
  }

  async function saveDraft() {
    if (!config) return;
    setSaving(true);
    try {
      await api.put('/site-content/draft', { config });
      setDirty(false);
      showToast('Modifications enregistrées');
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    if (!config) return;
    setPublishing(true);
    try {
      if (dirty) await api.put('/site-content/draft', { config });
      await api.post('/site-content/publish', { note: note || undefined });
      setDirty(false);
      setNote('');
      showToast('Modifications publiées');
    } finally {
      setPublishing(false);
    }
  }

  if (!config) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement…</div>;
  }

  function renderCategory(): ReactNode {
    switch (category) {
      case 'Général':
        return <GeneralSection config={config!} update={update} />;
      case 'Identité visuelle':
        return <BrandingSection config={config!} update={update} />;
      case 'Navigation':
        return <HeaderSection config={config!} update={update} />;
      case 'Hero':
        return <HeroSection config={config!} update={update} />;
      case 'Sections & ordre':
        return <SectionsOrderSection config={config!} update={update} />;
      case 'Logos partenaires':
        return <TrustLogosSection config={config!} update={update} />;
      case 'Chiffres':
        return <StatisticsSection config={config!} update={update} />;
      case 'Comment ça marche':
        return <HowItWorksSection config={config!} update={update} />;
      case 'Vidéo':
        return <VideoConfigSection config={config!} update={update} />;
      case 'Section Extras':
        return (
          <FeatureConfigSection
            title="Section Extras"
            data={config!.extrasSection}
            onChange={(patch) => update({ extrasSection: { ...config!.extrasSection, ...patch } })}
          />
        );
      case 'Section Restaurateurs':
        return (
          <FeatureConfigSection
            title="Section Restaurateurs"
            data={config!.restaurantsSection}
            onChange={(patch) => update({ restaurantsSection: { ...config!.restaurantsSection, ...patch } })}
          />
        );
      case 'Matching':
        return <MatchingSection config={config!} update={update} />;
      case 'Témoignages':
        return <TestimonialsSection config={config!} update={update} />;
      case 'Avant / après':
        return <BeforeAfterSection config={config!} update={update} />;
      case 'CTA final':
        return <FinalCtaSection config={config!} update={update} />;
      case 'FAQ':
        return <FaqSection config={config!} update={update} />;
      case 'Footer':
        return <FooterSection config={config!} update={update} />;
      case 'SEO':
        return <SeoSection config={config!} update={update} />;
      case 'Personnalisation avancée':
        return <AdvancedSection config={config!} update={update} />;
      case 'Historique':
        return <HistorySection onRestored={load} />;
      default:
        return null;
    }
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>
      <aside style={{ width: 220, borderRight: '1px solid var(--border)', background: 'var(--surface)', overflowY: 'auto', flexShrink: 0 }}>
        <div style={{ padding: '16px 16px 8px' }}>
          <Link to="/admin" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-secondary)', textDecoration: 'none', marginBottom: 12 }}>
            <ArrowLeft size={14} /> Retour au back-office
          </Link>
          <div style={{ fontWeight: 800, fontSize: 14 }}>Apparence du site</div>
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column', padding: '8px 8px 16px' }}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              style={{
                textAlign: 'left',
                padding: '9px 12px',
                borderRadius: 8,
                border: 'none',
                background: category === cat ? 'var(--accent-tint)' : 'transparent',
                color: category === cat ? 'var(--accent-dark)' : 'var(--text-secondary)',
                fontWeight: category === cat ? 700 : 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {cat}
            </button>
          ))}
        </nav>
      </aside>

      <main style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', maxWidth: 640 }}>
        {renderCategory()}
      </main>

      <section style={{ flex: 1, display: 'flex', flexDirection: 'column', borderLeft: '1px solid var(--border)', minWidth: 0 }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" className={viewport === 'desktop' ? 'btn btn-primary' : 'btn btn-secondary'} style={{ width: 34, height: 34, padding: 0 }} onClick={() => setViewport('desktop')} aria-label="Desktop">
              <Monitor size={15} />
            </button>
            <button type="button" className={viewport === 'tablet' ? 'btn btn-primary' : 'btn btn-secondary'} style={{ width: 34, height: 34, padding: 0 }} onClick={() => setViewport('tablet')} aria-label="Tablette">
              <Tablet size={15} />
            </button>
            <button type="button" className={viewport === 'mobile' ? 'btn btn-primary' : 'btn btn-secondary'} style={{ width: 34, height: 34, padding: 0 }} onClick={() => setViewport('mobile')} aria-label="Mobile">
              <Smartphone size={15} />
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {dirty && <span style={{ fontSize: 11.5, color: 'var(--warning)', fontWeight: 700 }}>Modifications non enregistrées</span>}
            <input
              className="input"
              placeholder="Note de publication (optionnel)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{ width: 200 }}
            />
            <button type="button" className="btn btn-secondary" style={{ width: 'auto', padding: '8px 14px' }} onClick={saveDraft} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer le brouillon'}
            </button>
            <button type="button" className="btn btn-primary" style={{ width: 'auto', padding: '8px 14px' }} onClick={publish} disabled={publishing}>
              {publishing ? 'Publication…' : 'Publier les modifications'}
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', background: '#e9e6e0', padding: 20, display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              width: VIEWPORTS[viewport],
              maxWidth: '100%',
              background: '#fff',
              borderRadius: viewport === 'desktop' ? 0 : 16,
              overflow: 'hidden',
              boxShadow: viewport === 'desktop' ? 'none' : '0 8px 32px rgba(0,0,0,0.15)',
              height: 'fit-content',
            }}
          >
            <SiteConfigRenderer config={config} />
          </div>
        </div>
      </section>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--text)', color: '#fff', padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 700, zIndex: 200 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
